import { useAuftragRepository } from '@Hooks/useRepository';
import type { ApiStorageTransaction, EnumStorageTransactionType } from '@Model';
import { StorageTransactionsRepository } from '@Repositories/Auftragsbearbeitung';
import { isOk, type Result, useToasts } from '@synqo/frontend-component-library';
import { useCallback } from 'react';

export interface BookingPrefill {
    storageLocationId?: string;
    materialId?: string;
    type?: EnumStorageTransactionType;
    occurredAt?: string;
}

/** Has the server prepare a booking draft with the Lagerort and Material it opens with and sets the type and Zeitpunkt. Undefined when that failed. */
// @ai-generated 2026-10-23T17:40:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function usePrepareBookingDraft() {
    const transactions = useAuftragRepository(StorageTransactionsRepository);
    const { pushAllToasts } = useToasts();

    return useCallback(
        async ({
            storageLocationId,
            materialId,
            type,
            occurredAt,
        }: BookingPrefill): Promise<ApiStorageTransaction | undefined> => {
            const created = await transactions.createPrefilledStorageTransaction({
                storage_location_id: storageLocationId,
                material_id: materialId,
            });
            pushAllToasts(created.messages);
            if (!isOk(created)) {
                return undefined;
            }
            const draft = created.data;

            const discardIfFailed = async (result: Result<unknown>) => {
                pushAllToasts(result.messages);
                if (isOk(result)) {
                    return false;
                }
                await transactions.delete(draft.id);
                return true;
            };

            if (type !== undefined || occurredAt !== undefined) {
                const updated = await transactions.update(
                    { ...draft, type: type ?? draft.type, occurred_at: occurredAt ?? draft.occurred_at },
                    draft.id,
                );
                if (await discardIfFailed(updated)) {
                    return undefined;
                }
            }

            return draft;
        },
        [transactions, pushAllToasts],
    );
}
